import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/*
|--------------------------------------------------------------------------
| Focus initial visual gate
|--------------------------------------------------------------------------
|
| The API batch must finish before the real Focus list can be revealed.
| After that, the list stays mounted behind the skeleton only until
| react-virtuoso confirms that the first visible cards are rendered.
|
| Image downloads and font readiness are intentionally NOT part of this
| gate. Waiting for them made the skeleton feel slow on mobile networks.
|
| Readiness is always tied to sourceKey, so a newly selected source can
| never inherit the ready state of the previous source for even one frame.
|
| Neighboring pages are prepared behind their own skeleton. They are marked
| ready only after Virtuoso confirms that real cards exist in the measured
| page scroller. Reaching the API-ready state alone must never reveal a blank
| neighboring page during a horizontal swipe.
|
*/

const MINIMUM_RENDERED_CARD_COUNT = 4;

const readySourceKeys = new Set();

const createReadinessState = (sourceKey, isReady = false) => ({
  sourceKey,
  isRenderedRangeReady: isReady,
  isVisualReady: isReady,
});

const revealOnNextPaint = (callback) => {
  if (typeof window === "undefined") {
    callback();
    return () => {};
  }

  const frameId = window.requestAnimationFrame(callback);

  return () => {
    window.cancelAnimationFrame(frameId);
  };
};

const useFocusInitialVisualReady = ({
  sourceKey,
  items = [],
  enabled = true,
  isInitialDataReady = false,
  scrollParent = null,
}) => {
  const activeSourceKeyRef = useRef(sourceKey);
  activeSourceKeyRef.current = sourceKey;

  const wasReadyOnFirstRender = readySourceKeys.has(sourceKey);

  const [readiness, setReadiness] = useState(() => {
    return createReadinessState(sourceKey, wasReadyOnFirstRender);
  });

  const currentReadiness =
    readiness.sourceKey === sourceKey
      ? readiness
      : createReadinessState(sourceKey, false);

  const isCachedReady = readySourceKeys.has(sourceKey);

  const requiredRenderedEndIndex = useMemo(() => {
    if (items.length === 0) return -1;

    const requiredCount = Math.min(
      items.length,
      MINIMUM_RENDERED_CARD_COUNT
    );

    return requiredCount - 1;
  }, [items.length]);

  const updateCurrentSourceReadiness = useCallback(
    (patch) => {
      setReadiness((previous) => {
        if (activeSourceKeyRef.current !== sourceKey) {
          return previous;
        }

        const base =
          previous.sourceKey === sourceKey
            ? previous
            : createReadinessState(sourceKey, false);

        return {
          ...base,
          ...patch,
          sourceKey,
        };
      });
    },
    [sourceKey]
  );

  /*
  |------------------------------------------------------------------------
  | Reset before a newly selected source can paint
  |------------------------------------------------------------------------
  */
  useLayoutEffect(() => {
    const isReady = readySourceKeys.has(sourceKey);
    setReadiness(createReadinessState(sourceKey, isReady));
  }, [sourceKey]);

  /*
  |------------------------------------------------------------------------
  | Empty results need no Virtuoso measurement pass
  |------------------------------------------------------------------------
  */
  useEffect(() => {
    if (!enabled || !isInitialDataReady) return;
    if (items.length !== 0) return;

    updateCurrentSourceReadiness({
      isRenderedRangeReady: true,
    });
  }, [
    enabled,
    isInitialDataReady,
    items.length,
    updateCurrentSourceReadiness,
  ]);

  /*
  |------------------------------------------------------------------------
  | Range changed — but only trust it if the scroller has real height
  |------------------------------------------------------------------------
  |
  | If Virtuoso's scroll parent has clientHeight === 0, range.endIndex
  | will be -1 or 0 even with data. In that case we must NOT mark ready.
  | The skeleton stays until the layout engine gives the scroller height.
  |
  */
  const handleRangeChanged = useCallback(
    (range) => {
      if (!enabled || !isInitialDataReady) return;
      if (activeSourceKeyRef.current !== sourceKey) return;
      if (requiredRenderedEndIndex < 0) return;

      // Guard the exact page-owned scroller. Using document.querySelector here
      // is unsafe because the looped pager can contain duplicate source pages.
      if (!scrollParent || scrollParent.clientHeight <= 0) {
        return;
      }

      if ((range?.endIndex ?? -1) >= requiredRenderedEndIndex) {
        updateCurrentSourceReadiness({
          isRenderedRangeReady: true,
        });
      }
    },
    [
      enabled,
      isInitialDataReady,
      requiredRenderedEndIndex,
      scrollParent,
      sourceKey,
      updateCurrentSourceReadiness,
    ]
  );

  /*
  |------------------------------------------------------------------------
  | Reveal on the next browser paint
  |------------------------------------------------------------------------
  */
  useEffect(() => {
    if (!enabled || !isInitialDataReady) return undefined;
    if (isCachedReady) return undefined;
    if (!currentReadiness.isRenderedRangeReady) return undefined;

    return revealOnNextPaint(() => {
      if (activeSourceKeyRef.current !== sourceKey) return;

      readySourceKeys.add(sourceKey);

      updateCurrentSourceReadiness({
        isVisualReady: true,
      });
    });
  }, [
    currentReadiness.isRenderedRangeReady,
    enabled,
    isCachedReady,
    isInitialDataReady,
    sourceKey,
    updateCurrentSourceReadiness,
  ]);

  const isSourceVisualReady =
    isCachedReady ||
    (readiness.sourceKey === sourceKey && readiness.isVisualReady);

  return {
    isVisualReady:
      enabled && isInitialDataReady ? isSourceVisualReady : false,
    isCachedReady,
    handleRangeChanged,
  };
};

export default useFocusInitialVisualReady;